import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Check,
  X,
  Clock,
  Download,
  Mail,
  Phone,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import CustomSelect from "../components/common/CustomSelect";

const AdminTournamentRegistrations = () => {
  const { tournamentId } = useParams();
  const { isDarkMode } = useTheme();

  // State
  const [registrations, setRegistrations] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [statistics, setStatistics] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    waitlist: 0,
    cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Load data
  useEffect(() => {
    loadRegistrations();
  }, [tournamentId, filterStatus, filterPayment]);

  const loadRegistrations = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        tournament_id: tournamentId,
        status: filterStatus,
        payment_status: filterPayment,
      });

      const response = await fetch(
        `https://api.fsgu.co.za/tournament-registrations.php?${params}`
      );

      const data = await response.json();

      if (data.success) {
        setRegistrations(data.data);
        setStatistics(data.statistics);

        // Get tournament details from first registration
        if (data.data.length > 0) {
          setTournament({
            name: data.data[0].tournament_name,
            date: data.data[0].tournament_date,
          });
        }
      }
    } catch (error) {
      console.error("Error loading registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update registration status
  const updateStatus = async (registrationId, newStatus, adminNotes = '') => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this registration?`)) {
      return;
    }

    try {
      const adminData = JSON.parse(localStorage.getItem('fsguAdmin'));
      
      const response = await fetch(
        'https://api.fsgu.co.za/update-registration.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'status',
            registration_id: registrationId,
            status: newStatus,
            admin_notes: adminNotes,
            confirmed_by: adminData?.id
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Registration status updated successfully!');
        loadRegistrations();
      } else {
        alert('Failed to update status: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update registration status');
    }
  };

  // Update payment status
  const updatePaymentStatus = async (registrationId, newPaymentStatus) => {
    if (!window.confirm(`Mark payment as ${newPaymentStatus}?`)) {
      return;
    }

    try {
      const response = await fetch(
        "https://api.fsgu.co.za/update-payment-status.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            registration_id: registrationId,
            payment_status: newPaymentStatus,
            payment_date:
              newPaymentStatus === "paid"
                ? new Date().toISOString().split("T")[0]
                : null,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        alert("Payment status updated successfully!");
        loadRegistrations();
      } else {
        alert("Failed to update payment status: " + data.message);
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      alert("Failed to update payment status");
    }
  };

  // Delete registration
  const deleteRegistration = async (registrationId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this registration? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `https://api.fsgu.co.za/tournament-registrations.php?registration_id=${registrationId}`,
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        alert("Registration deleted successfully!");
        loadRegistrations();
      } else {
        alert("Failed to delete: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting registration:", error);
      alert("Failed to delete registration");
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Player Name",
      "Email",
      "Phone",
      "Club",
      "Handicap",
      "Gender",
      "Age",
      "Status",
      "Payment Status",
      "Registered At",
    ];

    const rows = registrations.map((reg) => [
      reg.player_name,
      reg.player_email,
      reg.player_phone || "",
      reg.player_club || "",
      reg.player_handicap || "",
      reg.player_gender || "",
      reg.player_age || "",
      reg.status,
      reg.payment_status,
      new Date(reg.registered_at).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tournament-${tournamentId}-registrations-${Date.now()}.csv`;
    link.click();
  };

  // Filter registrations by search
  const filteredRegistrations = registrations.filter(
    (reg) =>
      reg.player_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.player_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.player_club &&
        reg.player_club.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Status badge
  const StatusBadge = ({ status }) => {
    const getBadgeClass = () => {
      switch(status) {
        case 'confirmed':
          return isDarkMode 
            ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
            : 'bg-fsgu-green-100 text-fsgu-green-700';
        case 'pending':
          return isDarkMode
            ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
            : 'bg-fsgu-gold-100 text-fsgu-gold-700';
        case 'waitlist':
          return isDarkMode
            ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400'
            : 'bg-fsgu-blue-100 text-fsgu-blue-700';
        case 'cancelled':
        case 'rejected':
        case 'no_show':
          return isDarkMode
            ? 'bg-red-900/30 text-red-400'
            : 'bg-red-100 text-red-700';
        default:
          return isDarkMode
            ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
            : 'bg-fsgu-silver-200 text-fsgu-silver-700';
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeClass()}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Payment badge
  const PaymentBadge = ({ status }) => {
    const getBadgeClass = () => {
      switch(status) {
        case 'paid':
          return isDarkMode
            ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
            : 'bg-fsgu-green-100 text-fsgu-green-700';
        case 'pending':
          return isDarkMode
            ? 'bg-fsgu-gold-900/30 text-fsgu-gold-400'
            : 'bg-fsgu-gold-100 text-fsgu-gold-700';
        case 'refunded':
          return isDarkMode
            ? 'bg-fsgu-blue-900/30 text-fsgu-blue-400'
            : 'bg-fsgu-blue-100 text-fsgu-blue-700';
        case 'waived':
          return isDarkMode
            ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
            : 'bg-fsgu-silver-200 text-fsgu-silver-700';
        default:
          return isDarkMode
            ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
            : 'bg-fsgu-silver-200 text-fsgu-silver-700';
      }
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getBadgeClass()}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
            Loading registrations...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm'
          : 'bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm'
      }`}>
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/admin/tournaments"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-fsgu-gray-800 text-fsgu-silver-300"
                    : "hover:bg-fsgu-silver-100 text-fsgu-silver-600"
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                }`}>
                  Registrations
                </h1>
                {tournament && (
                  <p className={`text-sm ${
                    isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                  }`}>
                    {tournament.name} • {new Date(tournament.date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Export Button */}
            <button onClick={exportToCSV} className="btn btn-outline inline-flex items-center">
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom py-6 space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
            }`}>
              Total
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
            }`}>
              {statistics.total}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
            }`}>
              Confirmed
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
            }`}>
              {statistics.confirmed}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
            }`}>
              Pending
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? "text-fsgu-gold-400" : "text-fsgu-gold-500"
            }`}>
              {statistics.pending}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
            }`}>
              Waitlist
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? "text-fsgu-blue-400" : "text-fsgu-blue-500"
            }`}>
              {statistics.waitlist}
            </div>
          </div>

          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}>
            <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
              isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-500"
            }`}>
              Cancelled
            </div>
            <div className={`text-3xl font-bold ${
              isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-500"
            }`}>
              {statistics.cancelled}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode
            ? "bg-fsgu-gray-800 border-fsgu-gray-700"
            : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
                isDarkMode ? "text-fsgu-silver-500" : "text-fsgu-silver-400"
              }`} />
              <input
                type="text"
                placeholder="Search by name, email, or club..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* Status Filter */}
            <CustomSelect
              name="status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: "all", label: "All Statuses" },
                { value: "confirmed", label: "Confirmed" },
                { value: "pending", label: "Pending" },
                { value: "waitlist", label: "Waitlist" },
                { value: "cancelled", label: "Cancelled" },
                { value: "rejected", label: "Rejected" },
              ]}
              placeholder="All Statuses"
            />

            {/* Payment Filter */}
            <CustomSelect
              name="payment"
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              options={[
                { value: "all", label: "All Payments" },
                { value: "paid", label: "Paid" },
                { value: "pending", label: "Payment Pending" },
                { value: "waived", label: "Waived" },
              ]}
              placeholder="All Payments"
            />
          </div>
        </div>

        {/* Registrations Table */}
        <div className={`rounded-xl border overflow-hidden ${
          isDarkMode
            ? "bg-fsgu-gray-800 border-fsgu-gray-700"
            : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
        }`}>
          {filteredRegistrations.length === 0 ? (
            <div className="p-12 text-center">
              <Users className={`w-16 h-16 mx-auto mb-4 ${
                isDarkMode ? "text-fsgu-silver-600" : "text-fsgu-silver-400"
              }`} />
              <p className={`text-lg font-medium mb-1 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                No registrations found
              </p>
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
              }`}>
                Try adjusting your filters
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`border-b ${
                  isDarkMode
                    ? "border-fsgu-gray-700 bg-fsgu-gray-900/50"
                    : "border-fsgu-silver-200 bg-fsgu-silver-50"
                }`}>
                  <tr>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Player
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Club
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Handicap
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Status
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Payment
                    </th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Registered
                    </th>
                    <th className={`px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  isDarkMode ? "divide-fsgu-gray-700" : "divide-fsgu-silver-200"
                }`}>
                  {filteredRegistrations.map((reg) => (
                    <tr
                      key={reg.id}
                      className={`transition-colors ${
                        isDarkMode
                          ? "hover:bg-fsgu-gray-700/50"
                          : "hover:bg-fsgu-silver-50"
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className={`font-semibold ${
                            isDarkMode
                              ? "text-fsgu-silver-100"
                              : "text-fsgu-silver-900"
                          }`}>
                            {reg.player_name}
                          </div>
                          <div className={`text-sm flex items-center mt-1 ${
                            isDarkMode
                              ? "text-fsgu-silver-400"
                              : "text-fsgu-silver-600"
                          }`}>
                            <Mail className="w-3 h-3 mr-1" />
                            {reg.player_email}
                          </div>
                          {reg.player_phone && (
                            <div className={`text-sm flex items-center ${
                              isDarkMode
                                ? "text-fsgu-silver-400"
                                : "text-fsgu-silver-600"
                            }`}>
                              <Phone className="w-3 h-3 mr-1" />
                              {reg.player_phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${
                          isDarkMode
                            ? "text-fsgu-silver-300"
                            : "text-fsgu-silver-700"
                        }`}>
                          {reg.player_club || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm font-medium ${
                          isDarkMode
                            ? "text-fsgu-silver-300"
                            : "text-fsgu-silver-700"
                        }`}>
                          {reg.player_handicap || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={reg.status} />
                      </td>
                      <td className="px-6 py-4">
                        <PaymentBadge status={reg.payment_status} />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${
                          isDarkMode
                            ? "text-fsgu-silver-400"
                            : "text-fsgu-silver-600"
                        }`}>
                          {new Date(reg.registered_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {/* Payment actions for pending payments */}
                          {reg.payment_status === "pending" && (
                            <button
                              onClick={() => updatePaymentStatus(reg.id, "paid")}
                              className={`p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-fsgu-green-400 hover:bg-fsgu-green-900/30'
                                  : 'text-fsgu-green-600 hover:bg-fsgu-green-50'
                              }`}
                              title="Mark as Paid"
                            >
                              <DollarSign className="w-4 h-4" />
                            </button>
                          )}

                          {/* Registration status actions */}
                          {reg.status === "pending" && (
                            <>
                              <button
                                onClick={() => updateStatus(reg.id, "confirmed")}
                                className={`p-2 rounded-lg transition-colors ${
                                  isDarkMode
                                    ? 'text-fsgu-green-400 hover:bg-fsgu-green-900/30'
                                    : 'text-fsgu-green-600 hover:bg-fsgu-green-50'
                                }`}
                                title="Confirm"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => updateStatus(reg.id, "rejected")}
                                className={`p-2 rounded-lg transition-colors ${
                                  isDarkMode
                                    ? 'text-red-400 hover:bg-red-900/30'
                                    : 'text-red-600 hover:bg-red-50'
                                }`}
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => {
                              setSelectedRegistration(reg);
                              setShowDetailsModal(true);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode
                                ? "text-fsgu-blue-400 hover:bg-fsgu-blue-900/30"
                                : "text-fsgu-blue-600 hover:bg-fsgu-blue-50"
                            }`}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteRegistration(reg.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              isDarkMode
                                ? 'text-red-400 hover:bg-red-900/30'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl ${
            isDarkMode ? "bg-fsgu-gray-800" : "bg-white"
          }`}>
            <div className={`p-6 border-b ${
              isDarkMode ? "border-fsgu-gray-700" : "border-fsgu-silver-200"
            }`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${
                  isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                }`}>
                  Registration Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`p-2 rounded-lg ${
                    isDarkMode
                      ? "hover:bg-fsgu-gray-700 text-fsgu-silver-400"
                      : "hover:bg-fsgu-silver-100 text-fsgu-silver-600"
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Player Info */}
              <div>
                <h3 className={`font-semibold mb-4 ${
                  isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                }`}>
                  Player Information
                </h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                      Name:
                    </dt>
                    <dd className={`font-medium ${
                      isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                    }`}>
                      {selectedRegistration.player_name}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                      Email:
                    </dt>
                    <dd className={`font-medium ${
                      isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                    }`}>
                      {selectedRegistration.player_email}
                    </dd>
                  </div>
                  {selectedRegistration.player_phone && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Phone:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.player_phone}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.player_club && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Club:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.player_club}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.player_handicap && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Handicap:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.player_handicap}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.player_gender && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Gender:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.player_gender}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.player_age && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Age:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.player_age}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Emergency Contact */}
              {(selectedRegistration.emergency_contact_name ||
                selectedRegistration.emergency_contact_phone) && (
                <div className={`pt-6 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <h3 className={`font-semibold mb-4 ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}>
                    Emergency Contact
                  </h3>
                  <dl className="space-y-3">
                    {selectedRegistration.emergency_contact_name && (
                      <div className="flex justify-between">
                        <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                          Name:
                        </dt>
                        <dd className={`font-medium ${
                          isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                        }`}>
                          {selectedRegistration.emergency_contact_name}
                        </dd>
                      </div>
                    )}
                    {selectedRegistration.emergency_contact_phone && (
                      <div className="flex justify-between">
                        <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                          Phone:
                        </dt>
                        <dd className={`font-medium ${
                          isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                        }`}>
                          {selectedRegistration.emergency_contact_phone}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Special Requirements */}
              {selectedRegistration.special_requirements && (
                <div className={`pt-6 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <h3 className={`font-semibold mb-3 ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}>
                    Special Requirements
                  </h3>
                  <p className={isDarkMode ? "text-fsgu-silver-300" : "text-fsgu-silver-700"}>
                    {selectedRegistration.special_requirements}
                  </p>
                </div>
              )}

              {/* Payment Information */}
              <div className={`pt-6 border-t ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}>
                <h3 className={`font-semibold mb-4 ${
                  isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                }`}>
                  Payment Information
                </h3>
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                      Status:
                    </dt>
                    <dd>
                      <PaymentBadge status={selectedRegistration.payment_status} />
                    </dd>
                  </div>
                  {selectedRegistration.payment_amount && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Amount:
                      </dt>
                      <dd className={`font-semibold text-lg ${
                        isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-600"
                      }`}>
                        R{selectedRegistration.payment_amount.toFixed(2)}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.payment_method && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Method:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.payment_method}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.payment_reference && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Reference:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {selectedRegistration.payment_reference}
                      </dd>
                    </div>
                  )}
                  {selectedRegistration.payment_date && (
                    <div className="flex justify-between">
                      <dt className={isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"}>
                        Payment Date:
                      </dt>
                      <dd className={`font-medium ${
                        isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                      }`}>
                        {new Date(selectedRegistration.payment_date).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                </dl>

                {/* Payment Actions */}
                {selectedRegistration.payment_status === "pending" && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        updatePaymentStatus(selectedRegistration.id, "paid");
                        setShowDetailsModal(false);
                      }}
                      className="btn btn-primary w-full"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Mark as Paid
                    </button>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              {selectedRegistration.admin_notes && (
                <div className={`pt-6 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <h3 className={`font-semibold mb-3 ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}>
                    Admin Notes
                  </h3>
                  <p className={isDarkMode ? "text-fsgu-silver-300" : "text-fsgu-silver-700"}>
                    {selectedRegistration.admin_notes}
                  </p>
                </div>
              )}
            </div>

            <div className={`p-6 border-t ${
              isDarkMode ? "border-fsgu-gray-700" : "border-fsgu-silver-200"
            }`}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournamentRegistrations; 